import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Printer } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthLabel = (m) =>
  new Date(`${m}-01T12:00:00`).toLocaleString('en-US', { month: 'long', year: 'numeric' });

/**
 * Printable one-page Budget Sheet — call window.print() from the button to
 * export a beautifully formatted PDF (browser Print → Save as PDF). No
 * server-side PDF library needed, and print CSS is scoped so nothing else
 * from the app leaks into the printed page.
 */
export default function BudgetPrintPage() {
  const { month } = useParams();
  const [data, setData] = useState(null);
  const [giving, setGiving] = useState(null);

  const load = useCallback(async () => {
    try {
      const [d, g] = await Promise.all([
        axios.get(`${API_URL}/api/budget/${month}`),
        axios.get(`${API_URL}/api/budget/giving-summary/${month.slice(0, 4)}`).catch(() => ({ data: null })),
      ]);
      setData(d.data);
      setGiving(g?.data || null);
    } catch { /* silent */ }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-[#36454F]/50 font-['Manrope']">Loading budget…</p>
      </div>
    );
  }

  const b = data.budget;
  const totalAllocated = b.categories.reduce((s, c) => s + (c.allocated || 0), 0);
  const totalSpent = data.total_spent || 0;
  const remaining = (b.income || 0) - totalAllocated;
  const catName = (id) => b.categories.find((c) => c.id === id)?.name || '—';

  return (
    <div className="print-root min-h-screen bg-[#F5F5F5] py-8" data-testid="budget-print-page">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-root { background: #fff !important; padding: 0 !important; }
          .print-sheet { box-shadow: none !important; border: none !important; padding: 24pt !important; margin: 0 !important; max-width: none !important; }
          @page { margin: 0.5in; }
        }
      `}</style>
      <div className="no-print max-w-3xl mx-auto px-4 mb-4 flex items-center justify-between">
        <a href={`/budget`} className="text-sm font-['Manrope'] text-[#B76E79] hover:underline" data-testid="print-back-link">
          ← Back to Budget
        </a>
        <Button
          onClick={() => window.print()}
          className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm"
          data-testid="print-trigger-btn"
        >
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.5} /> Print / Save as PDF
        </Button>
      </div>

      <div
        className="print-sheet max-w-3xl mx-auto bg-white border border-[#36454F]/10 shadow-sm p-10 font-['Manrope']"
        data-testid="print-sheet"
      >
        <div className="flex items-start justify-between border-b border-[#36454F]/15 pb-4 mb-6">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#B76E79] font-semibold">
              Selah Slate™ · Family Budget
            </p>
            <h1 className="font-['Playfair_Display'] text-3xl font-semibold text-[#36454F] mt-1">
              {data.household?.name || 'Household'}
            </h1>
            <p className="text-sm text-[#36454F]/60 mt-0.5">
              {monthLabel(month)} · {data.members?.map((m) => m.name.split(' ')[0]).join(', ')}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-widest text-[#36454F]/45 font-semibold">Printed</p>
            <p className="text-sm text-[#36454F]/70">{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3 mb-6">
          {[
            { l: 'Income', v: b.income },
            { l: 'Budgeted', v: totalAllocated },
            { l: 'Spent', v: totalSpent },
            { l: 'Left to allocate', v: remaining },
          ].map((s) => (
            <div key={s.l} className="border border-[#36454F]/10 rounded-sm p-3">
              <p className="text-[10px] uppercase tracking-wider text-[#36454F]/45 font-semibold">{s.l}</p>
              <p className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F] mt-0.5">{fmt(s.v)}</p>
            </div>
          ))}
        </div>

        <h2 className="font-['Playfair_Display'] text-lg font-semibold text-[#36454F] mb-2">Envelopes</h2>
        <table className="w-full text-sm border border-[#36454F]/10" data-testid="print-envelopes-table">
          <thead className="bg-[#F5F5F5] text-[#36454F]/60">
            <tr>
              <th className="text-left px-3 py-2 font-semibold uppercase text-[10px] tracking-wider">Envelope</th>
              <th className="text-right px-3 py-2 font-semibold uppercase text-[10px] tracking-wider">Allocated</th>
              <th className="text-right px-3 py-2 font-semibold uppercase text-[10px] tracking-wider">Spent</th>
              <th className="text-right px-3 py-2 font-semibold uppercase text-[10px] tracking-wider">Left</th>
            </tr>
          </thead>
          <tbody>
            {b.categories.map((c) => (
              <tr key={c.id} className="border-t border-[#36454F]/8">
                <td className="px-3 py-2 text-[#36454F]">
                  {c.is_giving && <span className="mr-1" title="Giving">✝</span>}
                  {c.name}
                </td>
                <td className="px-3 py-2 text-right">{fmt(c.allocated)}</td>
                <td className="px-3 py-2 text-right">{fmt(c.spent || 0)}</td>
                <td className="px-3 py-2 text-right">{fmt((c.allocated || 0) - (c.spent || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {(data.expenses || []).length > 0 && (
          <>
            <h2 className="font-['Playfair_Display'] text-lg font-semibold text-[#36454F] mt-6 mb-2">Activity feed</h2>
            <table className="w-full text-sm border border-[#36454F]/10" data-testid="print-activity-table">
              <thead className="bg-[#F5F5F5] text-[#36454F]/60">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold uppercase text-[10px] tracking-wider">Date</th>
                  <th className="text-left px-3 py-2 font-semibold uppercase text-[10px] tracking-wider">Member</th>
                  <th className="text-left px-3 py-2 font-semibold uppercase text-[10px] tracking-wider">Category</th>
                  <th className="text-left px-3 py-2 font-semibold uppercase text-[10px] tracking-wider">Note</th>
                  <th className="text-right px-3 py-2 font-semibold uppercase text-[10px] tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.expenses.map((e) => (
                  <tr key={e.expense_id} className="border-t border-[#36454F]/8">
                    <td className="px-3 py-2 text-[#36454F]/70">{(e.created_at || '').slice(0, 10)}</td>
                    <td className="px-3 py-2 text-[#36454F]/70">{e.added_by_name?.split(' ')[0] || 'Someone'}</td>
                    <td className="px-3 py-2 text-[#36454F]/70">{catName(e.category_id)}</td>
                    <td className="px-3 py-2 text-[#36454F]/60 italic">{e.note || '—'}</td>
                    <td className="px-3 py-2 text-right text-[#36454F]">{fmt(e.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {giving?.total > 0 && (
          <p className="mt-6 text-sm text-[#B76E79] font-['Playfair_Display'] italic">
            Giving Impact · {giving.year}: <strong>{fmt(giving.total)}</strong> given this year.
          </p>
        )}

        <p className="mt-8 pt-4 border-t border-[#36454F]/10 text-[11px] text-[#36454F]/50 italic text-center">
          &ldquo;The plans of the diligent lead to profit as surely as haste leads to poverty.&rdquo; — Proverbs 21:5 · Selah Slate — free forever.
        </p>
      </div>
    </div>
  );
}
